import React, { useState, useEffect, useCallback, useRef } from "react";
import { Input, InputGroup, InputGroupAddon, InputGroupText } from "reactstrap";
import { ICategoryGlobalListProps } from "../../../interfaces/Index";
import { _ } from "../../../utils/lib";
import "./CategoryGlobalList.scss";
import searchIcon from "../../../assets/images/search-icon.svg";
import closeIcon from "../../../assets/images/close.svg";
import ButtonComponent from "../../common/Button/ButtonComponent";
import {
  headerData,
  footerData,
  bodyDataAndKeyTypes,
} from "./CategoryTableConstants";
import { normalizeSelectedPayloadForEditFlow } from "../../../normalizer/CategoryGlobalNormalizer";
import Toaster from "../../common/Toaster/Toaster";
import LoaderComponent from "../../common/Loader/Loader";
import DataTableWithSubTable from "../../common/DataTableWithSubTable/DataTableWithSubTable";
import { Can } from "../../../config/Can";

export const doAPIsearch = (
  val: any,
  pageScrollPosition: any,
  fetchCategoriesGlobal: any
) => {
  if (val.length >= 3) {
    pageScrollPosition.current = 0;
    fetchCategoriesGlobal(false, {
      searchKey: val,
    });
  }
};

const CategoryGlobalList: React.FunctionComponent<ICategoryGlobalListProps> = (
  props: ICategoryGlobalListProps
) => {
  let pageScrollPosition: any = useRef(props.currentListPosition);

  const [isSearch, setIsSearching] = useState(
    props.retainedSearchText ? true : false
  );
  const [searchTerm, setSearchTerm] = useState(props.retainedSearchText);
  const [isLoadMore, setLoadMore] = useState(false);
  const [loadMoreText, setLoadMoreText] = useState({
    text: "Load more...",
    isLoading: false,
  });
  const [categoryGlobalData, setData] = useState<null | any>(null);
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  useEffect(() => {
    const {
      fetchCategoriesGlobal,
      categoriesGlobal,
      isCategoryGlobalAddedorUpdated,
    } = props;
    if (categoriesGlobal.length === 0 || isCategoryGlobalAddedorUpdated) {
      fetchCategoriesGlobal(false, { searchKey: searchTerm });
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, pageScrollPosition.current);
    }
  }, []);

  useEffect(() => {
    const { categoriesGlobal, pagination } = props;
    if (categoriesGlobal) {
      setData(categoriesGlobal);
      setTimeout(() => {
        window.scrollTo(
          0,
          window.pageYOffset - (window.pageYOffset - pageScrollPosition.current)
        );
      }, 250);
      if (pagination) {
        setLoadMore(true);
        setLoadMoreText({
          text: "Load more...",
          isLoading: false,
        });
      } else {
        setLoadMore(false);
      }
    }
  }, [props.categoriesGlobal]);

  useEffect(() => {
    const { error, isError } = props;
    if (isError && error) {
      alert((error! as any).errors);
    }
  }, [props.isError]);

  useEffect(() => {
    if (props.isCategoryGlobalAdded) {
      const message = "Category Global added successfully!";
      handleToaster(false, message);
    }
    if (props.isCategoryGlobalUpdated) {
      const message = "Category Global updated successfully!";
      handleToaster(false, message);
    } else {
      // const { error } = props;
      // if (isError && error) {
      // const message = (error! as any).errors;
      const { error, isError } = props;
      if (isError) {
        // alert((error! as any).errors);
        handleToaster(true, (error! as any).errors);
      }
      // handleToaster(true, message);
      // }
    }
  }, [props.isCategoryGlobalAdded, props.isCategoryGlobalUpdated]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  const debounceSearch = useCallback(
    _.debounce(
      (value: any) =>
        doAPIsearch(value, pageScrollPosition, props.fetchCategoriesGlobal),
      1000
    ),
    []
  );

  const handleSearchInputChange = (e: any) => {
    const { fetchCategoriesGlobal } = props;
    const value = e.target.value;
    setSearchTerm(value);
    debounceSearch(value);
    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    }
    if (value.length === 0 && isSearch) {
      fetchCategoriesGlobal(false);
    }
  };

  const editCategoryGlobal = (
    e: any,
    outerIndex?: number,
    innerIndex?: number
  ) => {
    const { addUpdatePageHandler } = props;

    addUpdatePageHandler(
      true,
      normalizeSelectedPayloadForEditFlow(
        categoryGlobalData[outerIndex as number][innerIndex as number]
      ),
      searchTerm,
      window.pageYOffset
    );
  };

  const closeSearch = () => {
    pageScrollPosition.current = 0;
    const { fetchCategoriesGlobal } = props;
    fetchCategoriesGlobal(false);
    setIsSearching(false);
    setSearchTerm("");
  };

  const loadFunc = () => {
    const { fetchCategoriesGlobal, pagination } = props;
    if (pagination) {
      pageScrollPosition.current = window.pageYOffset;
      setLoadMoreText({
        text: "Loading...",
        isLoading: true,
      });
      if (isSearch) {
        fetchCategoriesGlobal(false, {
          page: pagination,
          searchKey: searchTerm,
        });
      } else {
        fetchCategoriesGlobal(false, {
          page: pagination,
        });
      }
    }
  };

  return (
    <div className="data-table-container category-global-list-container">
      {props.isLoading && <LoaderComponent />}
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>
      <section className="relative container sticky-top">
        <section className="row">
          <section className="col-9">
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <img src={searchIcon} alt="close" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                className="search-input"
                placeholder="Search by name or ID"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
              <div
                className={isSearch ? "close-button" : "hide close-button"}
                onClick={closeSearch}
              >
                <img src={closeIcon} alt="close" />
              </div>
            </InputGroup>
          </section>
          <section className="col-3">
            <Can I="create" a="category">
              <ButtonComponent
                buttonText="Add a category"
                onClickHandler={() =>
                  props.addUpdatePageHandler(
                    false,
                    null,
                    searchTerm,
                    window.pageYOffset
                  )
                }
                buttonClass="btn-primary btn btn-secondary button-large"
              />
            </Can>
          </section>
        </section>
      </section>
      <Can I="read" a="category">
        <DataTableWithSubTable
          tableId="category-global-list"
          tableHeaderData={headerData}
          tableFooterData={footerData}
          tableBodyDataTypes={bodyDataAndKeyTypes}
          tableBodyRowData={categoryGlobalData}
          noSearchResultFlag={isSearch}
          editRowHandler={editCategoryGlobal}
        />
        {isLoadMore && (
          <div id="load-more-container">
            <ButtonComponent
              buttonText={loadMoreText.text}
              buttonClass="btn-primary-outline button-medium"
              onClickHandler={loadFunc}
              isDisable={loadMoreText.isLoading}
            />
          </div>
        )}
      </Can>
    </div>
  );
};

export default CategoryGlobalList;
